import React, { useMemo, useState } from "react";
import CustomTable from "components/Table/CustomTable";
import dayjs from "dayjs";
import { ReactComponent as EditIcon } from "assets/icons/edit.svg";
import { useModal } from "contexts/ModalProvider";
import { CommissionsForm } from "./CommissionsForm";
import { formatCurrency } from "utils/formatters";
import { CommissionsSchema } from "model/commissions/commissions.schema";
import { useToasts } from "utils/useHotToast";
import check from "assets/icons/check-circle.svg";
import slash from "assets/icons/slash-circle.svg";
import ToastNotification from "components/Toast/ToastNotification";
import Image from "components/Common/Image";
import { getImageUrl, ImageSourceType } from "utils/imageUtils";

import { useDeleteCommissionsMutation, useUpdateCommissionMutation } from "services/commissions/api";

// Custom Hooks
import useTableChangeHandler from "hooks/useTableChangeHandler";

const CommissionsTable = ({ data, isLoading, error, onTableChange }) => {
  const { modal } = useModal();
  const { addToast, removeToast } = useToasts();
  const [selectedCommissions, setSelectedCommissions] = useState([]);

  // API Hooks
  const [deleteCommissions] = useDeleteCommissionsMutation();
  const [updateCommission] = useUpdateCommissionMutation();

  // HOOK CHANGE
  const { handleSort, handleFilter, handleSearch, handlePageChange, forceRefresh } = useTableChangeHandler(onTableChange);

  const columns = useMemo(
    () => [
      {
        header: "รายการ Product Affiliate",
        accessor: "name",
        sortable: true,
        render: (item) => (
          <div className="product-info d-flex align-items-center gap-3">
            <Image src={getImageUrl(item.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.name} />
            <div>
              <div className="text-lg fw-semibold">{item.name}</div>
              <div className="text-md text-gray-500">ID: {item.sku}</div>
            </div>
          </div>
        ),
      },
      {
        header: "แบรนด์ F&N",
        accessor: "brand",
        responsive: "md",
        render: (item) => (
          <div className="brand-info d-flex align-items-center gap-3">
            <Image src={getImageUrl(item.brand?.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.brand?.name} />
            <div className="text-lg">{item.brand?.name}</div>
          </div>
        ),
      },
      {
        header: "หมวดหมู่สินค้า",
        accessor: "category",
        responsive: "lg",
        render: (item) => <div className="text-success-500 fw-semibold">{item.category?.name}</div>,
      },
      {
        header: "ราคา",
        accessor: "price",
        sortable: true,
        align: "end",
        render: (item) => formatCurrency(item.pricePromotion ? item.pricePromotion : item.price),
      },
      {
        header: "คอมมิชชันที่ได้",
        accessor: "commission",
        align: "end",
        render: (item) => {
          return <div className="fw-semibold text-primary-600">{formatCurrency(item.commission?.commission ?? 0)}</div>;
        },
      },
      {
        header: "วันที่สร้างสินค้า",
        accessor: "createdDate",
        responsive: "xl",
        render: (item) => dayjs(item.createdAt).format("YYYY-MM-DD"),
      },
      {
        header: "",
        accessor: "actions",
        width: 50,
        render: (item) => (
          <div
            className="text-gray-500 cursor-pointer"
            onClick={(event) => {
              event.stopPropagation();
              handleEditCommissions(item);
            }}
          >
            <EditIcon />
          </div>
        ),
      },
    ],
    []
  );

  const handleEditCommissions = async (item) => {
    let formValue = null;
    const initialValidity = Boolean(item.commission?.commissionRate);
    let result;
    try {
      result = await modal.confirm.primary({
        title: "ตั้งค่า-ค่าคอมมิชชัน",
        message: renderMessage(item),
        confirmText: "ยืนยันค่าคอมมิชชันใหม่",
        cancelText: "ยกเลิก",
        isValid: initialValidity,
        children: (
          <CommissionsForm
            initialValue={item.commission?.commissionRate}
            onSubmit={(value) => {
              formValue = value;
            }}
            onValidationChange={(valid) => {
              modal.setValidity(valid);
            }}
          />
        ),
        size: "lg",
      });
    } finally {
      modal.setValidity(true);
    }
    if (result && formValue) {
      try {
        const response = await updateCommission({
          id: item.id,
          commissionRate: formValue,
        });

        const toastId = addToast(<ToastNotification variant="success" icon={check} title="สำเร็จ" description="อัพเดทค่าคอมมิชชันเรียบร้อยแล้ว" onSecondaryClick={() => removeToast(toastId)} />, { appearance: "success", autoDismiss: true });

        // Refresh data
        forceRefresh();
      } catch (error) {
        addToast(<ToastNotification variant="danger" icon={slash} title="ไม่สำเร็จ" description="เกิดข้อผิดพลาดในการอัพเดทค่าคอมมิชชัน กรุณาลองใหม่อีกครั้ง" />, { appearance: "error", autoDismiss: true });
      }
    } else if (result && !formValue) {
      addToast(<ToastNotification icon={slash} title="ไม่สำเร็จ" description="กรุณาระบุค่าคอมมิชชันที่ต้องการ" />, { appearance: "warning", autoDismiss: true });
    }
  };

  const renderMessage = (item) => {
    return (
      <div className="product-info d-flex align-items-start gap-3">
        <Image src={getImageUrl(item.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.name} />
        <div className="d-flex flex-column gap-1">
          <div className="d-flex align-items-center">
            <div className="text-xl fw-semibold">{item.productName}</div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div className="text-lg text-gray-500">{`รหัสสินค้า : ${item.id}`}</div>
            <div className="text-lg text-gray-500">{`SKU : ${item.sku}`}</div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div className="text-lg text-gray-500">{`แบรนด์ : ${item.brand.name}`}</div>
            <div className="text-lg text-gray-500">{`หมวดหมู่สินค้า : ${item.category.name}`}</div>
          </div>
          <div className="text-xl fw-semibold text-primary-500">{`ค่าคอมมิชชัน : ${item.commission?.commissionRate ?? 0}%`}</div>
        </div>
      </div>
    );
  };

  const handleSelectionChange = (selectedIds) => {
    setSelectedCommissions(selectedIds);
  };

  const handleDeleteCommissions = async () => {
    if (selectedCommissions.length === 0) {
      addToast("กรุณาเลือกรายการที่ต้องการลบ", {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    const result = await modal.alert.danger({
      title: "ยืนยันการลบรายการคอมมิชชัน",
      message: `คุณต้องการลบรายการคอมมิชชันที่เลือกไว้จำนวน ${selectedCommissions.length} รายการหรือไม่?`,
      confirmText: "ยืนยันการลบ",
      cancelText: "ยกเลิก",
      children: (
        <div className="d-flex flex-column gap-3 divide-y">
          {selectedCommissions.map((item, index) => (
            <div key={item.productId} className={`d-flex flex-column gap-2 ${index > 0 && "pt-3"}`}>
              <div className="product-info d-flex align-items-center gap-3">
                <img src={item.productImgUrl} alt={item.productName} className="image" />
                <div>
                  <div className="text-lg fw-semibold">{item.productName}</div>
                  <div className="text-md text-gray-500">SKU: {item.sku}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ),
    });

    if (result) {
      try {
        const response = await deleteCommissions(selectedCommissions.map((item) => item.productId));
        const toastId = addToast(<ToastNotification variant="success" icon={check} title={`นำรายการคอมมิชชันที่เลือกออกจากระบบแล้ว ${items.length} รายการ`} description="รายการคอมมิชชันจะไม่สามารถนำกลับมาได้" primaryButtonText="ดูสินค้าจาก NomAroi" onPrimaryClick={() => removeToast(toastId)} onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });

        setSelectedCommissions([]);

        if (onTableChange) {
          onTableChange({
            type: "refresh",
          });
        }
      } catch (error) {
        const toastId = addToast(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถลบรายการคอมมิชชันออกจากรายการได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
      }
    }
  };

  return (
    <div>
      <div className="text-lg mb-4">ค้นพบรายการ Product Affiliate ในระบบทั้งหมด *{data?.pagination?.total?.toLocaleString() || 0} รายการ *</div>

      <CustomTable
        keyField="productId"
        data={data?.data || []}
        columns={columns}
        selectable={false}
        onSelectionChange={handleSelectionChange}
        selectionActions={
          <a className="danger" onClick={handleDeleteCommissions}>
            <i className="bx bx-trash" />
            <span className="d-none d-md-block">ลบรายการที่เลือกไว้</span>
          </a>
        }
        // onRowClick={(item) => console.log("Row clicked:", item)}
        className="commissions-table"
        itemsPerPage={data?.pagination?.limit || 10}
        totalItems={data?.pagination?.total || 0}
        onSort={handleSort}
        onFilter={handleFilter}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        status={isLoading ? "loading" : error ? "error" : data?.data?.length === 0 ? "empty" : "normal"}
        errorMessage={error?.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"}
        emptyMessage="ไม่พบข้อมูลสินค้า Affiliate"
      />
    </div>
  );
};

const withYupValidation = (WrappedComponent, schema) => {
  return function WithYupValidation(props) {
    // Skip validation if loading or error
    if (props.isLoading || props.error) {
      return <WrappedComponent {...props} />;
    }

    try {
      schema.validateSync(props.data);
      return <WrappedComponent {...props} />;
    } catch (error) {
      return (
        <div className="alert alert-danger">
          <strong>Validation Error:</strong> {error.message}
        </div>
      );
    }
  };
};

const ValidatedTable = withYupValidation(CommissionsTable, CommissionsSchema);

export default ValidatedTable;
